// The signature layer: the signature order looks and the deep run shroud
// (R2408, R2409, `ui-map-interface.md` §6.1.12, mockup round 105).
//
// **What starts a look.** The SIGNATURE event of a spend starts the spend
// look (card 01) for every viewer the event reaches. The hold looks read
// `signature_active`, `signature_left_sec` and `signature_order`, which
// every viewer that hears the hull gets (R2406). The owner's plan reads
// what the owner alone gets: `signature_target` and the course end for the
// lane of OUTFLANK, the sonar reach for the footprint of CRASH DIVE, and
// `deep_run` for the shroud. A hull the layer first sees while its order
// already holds shows the hold at once and plays no onset.
//
// **Which shots are the order's shots.** CANISTER and BROADSIDE hold for
// their window, and every gun shot of the hull inside it is a shot of the
// order (R2856). The hub sends the events of a tick before the State frame
// of that tick, so the gun HIT of a shot on the last tick of the window
// reaches the client while the client still reads the order as holding.
// Each gun HIT whose source point lies inside `shot_match_px` of a hull
// whose CANISTER or BROADSIDE holds is a shot of the order: it fires the
// pellet fan or flashes the gun ports again.
//
// **The split with the fleet layer.** Three looks change marks the fleet
// layer owns: CANISTER's iris cuts the dead zone hatch, DIG IN closes the
// rear dead zone, and CRASH DIVE fades the hull and dots its wake. The
// fleet layer asks this layer for the share of each (`SignatureMarks`) and
// draws them in its own batches; every other mark draws here.
//
// The slot `signature` stands right after `gatherover`, so a look reads
// over the hulls and under the rounds, the blasts and the hits. It draws
// through one quad, one circle and one segment batch of the engine, so
// both backends draw it, and a frame with no look packs nothing.

import { EventKind, SignatureOrder } from '../../gen/proto/aquaphobia';
import type { Event as WireEvent, FleetState } from '../../gen/proto/aquaphobia';
import { FACTION_MARKS, SIGNATURE as S } from '../../gen/presentation';
import { factionColor, MPX_PER_PX } from '../../world';
import { F_ROUND, outside, type DrawCtx, type FrameInfo, type Layer, type ViewRect } from '../layer';
import type { BatchSource } from './batches';
import { outCubic } from '../../render/recipe-kit';
import { clamp01 } from '../../render/math';
import { sternSetEdge } from '../../render/aspect-chevrons';
import {
  countdownShare, footRadius, ghostAlpha, holdShare, packBows, packBubbles, packClamp, packEnd,
  packCrates, packFootprint, packGlance, packHold, packLane, packPellets, packPorts, packRunWake, packShroud,
  packSpend, packSternMark, packTrace, runShare, sideOf, sternMark,
  type SignatureMarks, type SignaturePose, type SignatureSinks,
} from '../../render/signature-looks';

/** Where the layer reads the world. */
export interface SignatureFeeds {
  /** One hull as the fleet layer draws it, written into `out`; false when
   *  the fleet layer holds no such hull. */
  pose(id: number, out: SignaturePose): boolean;
  /** The nearest visible hull of another faction inside `r`, or null. */
  foe(x: number, y: number, faction: number, r: number): { x: number; y: number } | null;
  /** The order of a class (`ShipSpec.signature`), for a server that sends
   *  the active bit and no order. */
  orderOf(cls: number): SignatureOrder;
  /** `CatalogRules.approach.crash_dive_noise_mul`, 0 before a hello. */
  noiseMul(): number;
  /** Map seconds for one wall second: the tactical scale. */
  mapSecPerWallSec(): number;
}

/** How many outline ghosts trail an OUTFLANK hull, and how many hits a
 *  BRACE hull keeps for its glances. */
const GHOSTS = 3;
const HITS = 4;
/** The trail points an OUTFLANK hull keeps: enough to reach the far ghost
 *  at a mark scale of 1, the largest (R2420). */
const TRAIL = Math.ceil((GHOSTS * S.ghostGapPx) / S.ghostSamplePx) + 2;

/** The outer edge of the marks astern of an OUTFLANK target: the charge
 *  pip (§6.1.8) or the stern chevrons (§6.1.11), whichever stands further
 *  out (R2417). The stern mark stands past it. */
const ASTERN_EDGE = Math.max(
  FACTION_MARKS.hull.asternPx + FACTION_MARKS.hull.sizePx / 2, sternSetEdge(),
);

/** How long a record stays after its last moment, so every end look, fade
 *  and dark port runs out (wall seconds). */
const LINGER = Math.max(
  S.spendSec, S.endSec, S.traceSec, S.glanceSec, S.pelletSec, S.laneFadeSec,
  S.irisCloseSec, S.rearCloseSec, S.chevronFoldSec + 2 * S.chevronStepSec,
  S.portGlowSec, S.diveFadeSec, S.footEndSec, S.footGrowSec, S.shroudOutSec, S.bubbleSec,
  S.getawayCrateSec, S.getawayWakeOutSec,
);

/** One hull with a look, on the layer's own wall clock. NaN is "never". */
interface Rec {
  id: number;
  own: boolean;
  /** The order of the hold, or of the last hold. */
  order: SignatureOrder;
  active: boolean;
  /** When the hold began (-Infinity for a hold the layer saw already
   *  standing) and when it ended. */
  onAt: number;
  offAt: number;
  /** The map seconds left of a timed order, when they arrived, and the
   *  first figure after the spend (0 for MUSTER, which has no clock). */
  left: number;
  leftAt: number;
  first: number;
  /** The hull an OUTFLANK sails to (own hulls only). */
  target: number;
  /** When the hull reached the stern point. */
  arriveAt: number;
  /** The spend: when, which order, and the drawn bow at that moment. */
  spendAt: number;
  spendOrder: SignatureOrder;
  fromHead: number;
  /** Where the hull stood at the spend: the point the GETAWAY cargo went
   *  down (R2857). */
  fromX: number;
  fromY: number;
  /** The last shot of CANISTER or BROADSIDE: when and toward which
   *  bearing; the side of the ports. */
  shotAt: number;
  shotAng: number;
  side: number;
  /** The DEEP RUN gait (own hulls only). */
  deep: boolean;
  deepOnAt: number;
  deepOffAt: number;
  /** The trail points, `x, y, head` newest first, and when the trail took
   *  its last point. */
  ghosts: Float32Array;
  ghostN: number;
  ghostAt: number;
  /** The hits a BRACE hull took: `at, sourceX, sourceY` in a ring. */
  hits: Float64Array;
  hitN: number;
}

function newRec(id: number): Rec {
  return {
    id, own: false, order: SignatureOrder.SIGNATURE_ORDER_NONE, active: false,
    onAt: NaN, offAt: NaN, left: 0, leftAt: 0, first: 0, target: 0, arriveAt: NaN,
    spendAt: NaN, spendOrder: SignatureOrder.SIGNATURE_ORDER_NONE, fromHead: 0, fromX: 0, fromY: 0,
    shotAt: NaN, shotAng: 0, side: 1,
    deep: false, deepOnAt: NaN, deepOffAt: NaN,
    ghosts: new Float32Array(TRAIL * 3), ghostN: 0, ghostAt: -Infinity,
    hits: new Float64Array(HITS * 3), hitN: 0,
  };
}

/** A hold was seen: it stands now, or it ended in the layer's sight. */
function held(r: Rec): boolean {
  return r.active || !Number.isNaN(r.offAt);
}

/** The latest of the moments a record keeps, NaN ignored. */
function lastMoment(r: Rec): number {
  let m = -Infinity;
  if (r.spendAt > m) m = r.spendAt;
  if (r.offAt > m) m = r.offAt;
  if (r.shotAt > m) m = r.shotAt;
  if (r.deepOffAt > m) m = r.deepOffAt;
  return m;
}

function blankPose(): SignaturePose {
  return { x: 0, y: 0, head: 0, faction: 0, cls: 0, own: false, alpha: 0, sonarPx: 0, tx: 0, ty: 0, k: 1, cell: '' };
}

export class SignatureLayer implements Layer, SignatureMarks {
  readonly name = 'signature';
  private shown = true;
  /** The layer's own clock in wall seconds. */
  private time = 0;
  private readonly recs = new Map<number, Rec>();
  /** Every hull a State frame named, so a hold or a gait the layer sees
   *  begin is told from one it sees on a sighting. */
  private readonly known = new Set<number>();
  private readonly sinks: SignatureSinks;
  private readonly me: SignaturePose = blankPose();
  private readonly them: SignaturePose = blankPose();
  private readonly markAt = { x: 0, y: 0 };
  private cull: ViewRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  /** The pass over one record, bound once, so a frame allocates no
   *  closure (R131). */
  private readonly packOne = (r: Rec): void => this.packRec(r);

  constructor(src: BatchSource, private readonly feeds: SignatureFeeds) {
    this.sinks = {
      quads: src.newQuads(32),
      rings: src.newCircles(128),
      segs: src.newSegments(256),
      sprite: (key) => src.sprite(key),
    };
  }

  /** How many hulls hold a record now. The tests read it. */
  get records(): number { return this.recs.size; }

  /** A hello is a new world: every look leaves the water. */
  clear(): void {
    this.recs.clear();
    this.known.clear();
  }

  // -- the wire --------------------------------------------------------------

  /** One State frame (a delta, or `full`). */
  applyState(fleets: readonly FleetState[], removed: readonly number[], full: boolean): void {
    if (full) {
      const ids = new Set<number>();
      for (const w of fleets) ids.add(w.id);
      for (const id of [...this.known]) if (!ids.has(id)) this.forget(id);
      for (const id of [...this.recs.keys()]) if (!ids.has(id)) this.recs.delete(id);
    }
    for (const id of removed) this.forget(id);
    for (const w of fleets) this.observe(w);
  }

  private forget(id: number): void {
    this.known.delete(id);
    this.recs.delete(id);
  }

  private observe(w: FleetState): void {
    const seen = this.known.has(w.id);
    this.known.add(w.id);
    let r = this.recs.get(w.id);
    if (r === undefined) {
      if (!w.signatureActive && !w.deepRun) return;
      r = newRec(w.id);
      this.recs.set(w.id, r);
    }
    const t = this.time;
    r.own = w.own;
    if (w.signatureActive) {
      if (!r.active) {
        r.active = true;
        r.onAt = seen ? t : -Infinity;
        r.offAt = NaN;
        r.first = w.signatureLeftSec;
        r.left = w.signatureLeftSec;
        r.leftAt = t;
        r.ghostN = 0;
        r.hitN = 0;
        r.arriveAt = NaN;
        r.shotAt = NaN;
      }
      r.order = w.signatureOrder !== SignatureOrder.SIGNATURE_ORDER_NONE
        ? w.signatureOrder : this.feeds.orderOf(w.cls);
      if (w.signatureLeftSec !== r.left) { r.left = w.signatureLeftSec; r.leftAt = t; }
      if (r.left > r.first) r.first = r.left;
      r.target = w.signatureTarget;
    } else if (r.active) {
      r.active = false;
      r.offAt = t;
      r.target = 0;
    }
    if (w.deepRun && !r.deep) {
      r.deep = true;
      r.deepOnAt = seen ? t : -Infinity;
      r.deepOffAt = NaN;
    } else if (!w.deepRun && r.deep) {
      r.deep = false;
      r.deepOffAt = t;
    }
  }

  /** One event burst: the spends and the gun hits. */
  applyEvents(events: readonly WireEvent[]): void {
    for (const e of events) {
      if (e.kind === EventKind.EVENT_KIND_SIGNATURE) this.spend(e);
      else if (e.kind === EventKind.EVENT_KIND_HIT) this.hit(e);
    }
  }

  private spend(e: WireEvent): void {
    if (e.fleet === 0) return;
    let r = this.recs.get(e.fleet);
    if (r === undefined) {
      r = newRec(e.fleet);
      this.recs.set(e.fleet, r);
    }
    r.spendAt = this.time;
    r.spendOrder = e.signature;
    const seen = this.feeds.pose(e.fleet, this.me);
    r.fromHead = seen ? this.me.head : 0;
    // The event carries the point of the spend as well, for a hull the
    // fleet layer does not hold yet.
    r.fromX = seen ? this.me.x : e.xMpx / MPX_PER_PX;
    r.fromY = seen ? this.me.y : e.yMpx / MPX_PER_PX;
  }

  /**
   * One gun HIT: a glance on a BRACE hull it struck, and a shot of the
   * CANISTER or BROADSIDE of the hull that fired it (the head of this file).
   */
  private hit(e: WireEvent): void {
    const t = this.time;
    const sx = e.sourceXMpx / MPX_PER_PX, sy = e.sourceYMpx / MPX_PER_PX;
    const v = this.recs.get(e.victim);
    if (v !== undefined && v.active && v.order === SignatureOrder.SIGNATURE_ORDER_BRACE) {
      const j = (v.hitN % HITS) * 3;
      v.hits[j] = t; v.hits[j + 1] = sx; v.hits[j + 2] = sy;
      v.hitN++;
    }
    const vx = e.xMpx / MPX_PER_PX, vy = e.yMpx / MPX_PER_PX;
    for (const r of this.recs.values()) {
      const canister = r.order === SignatureOrder.SIGNATURE_ORDER_CANISTER;
      const broadside = r.order === SignatureOrder.SIGNATURE_ORDER_BROADSIDE;
      if (!canister && !broadside) continue;
      if (!this.feeds.pose(r.id, this.me)) continue;
      const p = this.me;
      if (Math.hypot(p.x - sx, p.y - sy) > S.shotMatchPx) continue;
      if (r.active) {
        r.shotAt = t;
        r.shotAng = Math.atan2(vy - p.y, vx - p.x);
        if (broadside) r.side = sideOf(p.x, p.y, p.head, vx, vy);
      }
      return;
    }
  }

  // -- the shares the fleet layer reads (SignatureMarks) ----------------------

  digClose(fleetId: number): number {
    const r = this.recs.get(fleetId);
    if (r === undefined || r.order !== SignatureOrder.SIGNATURE_ORDER_DIG_IN || !held(r)) return -1;
    return holdShare(this.onAge(r), this.offAge(r), S.rearCloseSec, S.rearCloseSec);
  }

  irisOpen(fleetId: number): number {
    const r = this.recs.get(fleetId);
    if (r === undefined || r.order !== SignatureOrder.SIGNATURE_ORDER_CANISTER || !held(r)) return 0;
    return holdShare(this.onAge(r), this.offAge(r), S.irisOpenSec, S.irisCloseSec);
  }

  diveShare(fleetId: number): number {
    const r = this.recs.get(fleetId);
    if (r === undefined || !r.own || r.order !== SignatureOrder.SIGNATURE_ORDER_CRASH_DIVE || !held(r)) return 0;
    return holdShare(this.onAge(r), this.offAge(r), S.diveFadeSec, S.diveFadeSec);
  }

  private onAge(r: Rec): number { return this.time - r.onAt; }

  /** The wall seconds since the hold ended, -1 while it stands. */
  private offAge(r: Rec): number { return r.active ? -1 : this.time - r.offAt; }

  // -- the frame -------------------------------------------------------------

  update(frame: FrameInfo): void {
    this.time += Math.min(frame.dtMs, 100) / 1000;
    this.cull = frame.cull;
    this.sinks.quads.begin();
    this.sinks.rings.begin();
    this.sinks.segs.begin();
    if (this.recs.size > 0) this.recs.forEach(this.packOne);
  }

  private packRec(r: Rec): void {
    const t = this.time;
    if (!r.active && !r.deep && t - lastMoment(r) > LINGER) {
      this.recs.delete(r.id);
      return;
    }
    const p = this.me;
    if (!this.feeds.pose(r.id, p)) return;
    const pad = S.cullPadPx, c = this.cull;
    if (!p.own && outside(p.x, p.y, c.x0 - pad, c.y0 - pad, c.x1 + pad, c.y1 + pad)) return;
    const rings = this.sinks.rings;
    const ink = factionColor(p.faction), k = p.k, x = p.x, y = p.y;
    // Card 01: the spend, the hold and the end.
    const su = runShare(t - r.spendAt, S.spendSec);
    if (su >= 0) packSpend(rings, x, y, su, ink, k);
    const timed = r.first > 0;
    const left = timed
      ? countdownShare(r.left, r.first, t - r.leftAt, this.feeds.mapSecPerWallSec()) : 1;
    if (r.active) packHold(rings, x, y, p.head, timed ? left : null, t, ink, k);
    else {
      const eu = runShare(t - r.offAt, S.endSec);
      if (eu >= 0) packEnd(rings, x, y, eu, ink, k);
    }
    if (p.own) this.packDeep(r, p);
    if (held(r) || r.spendOrder !== SignatureOrder.SIGNATURE_ORDER_NONE) this.packOrder(r, p, left, ink);
  }

  /** The look of each order, after the snap ring. */
  private packOrder(r: Rec, p: SignaturePose, left: number, ink: number): void {
    const { rings, segs } = this.sinks;
    const t = this.time, k = p.k, x = p.x, y = p.y;
    const on = this.onAge(r), off = this.offAge(r);
    switch (r.order !== SignatureOrder.SIGNATURE_ORDER_NONE ? r.order : r.spendOrder) {
      case SignatureOrder.SIGNATURE_ORDER_OUTFLANK:
        if (r.active) this.packOutflank(r, p, ink);
        return;
      case SignatureOrder.SIGNATURE_ORDER_BRACE: {
        const tu = runShare(t - r.spendAt, S.traceSec);
        if (tu >= 0) packTrace(rings, segs, x, y, r.fromHead, p.head, tu, ink, k, F_ROUND);
        const eu = r.active ? 0 : runShare(off, S.endSec);
        if (held(r) && eu >= 0) {
          packClamp(rings, segs, x, y, p.head, outCubic(on / S.clampCloseSec), left, 1 - eu, ink, k, F_ROUND);
        }
        if (!r.active) return;
        for (let j = 0; j < Math.min(r.hitN, HITS); j++) {
          const gu = runShare(t - r.hits[j * 3], S.glanceSec);
          if (gu < 0) continue;
          packGlance(segs, x, y, Math.atan2(r.hits[j * 3 + 2] - y, r.hits[j * 3 + 1] - x), gu, k, F_ROUND);
        }
        return;
      }
      case SignatureOrder.SIGNATURE_ORDER_CANISTER: {
        const pu = runShare(t - r.shotAt, S.pelletSec);
        if (pu >= 0) packPellets(rings, x, y, r.shotAng, pu, ink, k);
        return;
      }
      case SignatureOrder.SIGNATURE_ORDER_DIG_IN:
        if (held(r)) packBows(segs, x, y, p.head, on, off, ink, k, F_ROUND);
        return;
      case SignatureOrder.SIGNATURE_ORDER_BROADSIDE:
        this.packBroadside(r, p, ink);
        return;
      case SignatureOrder.SIGNATURE_ORDER_GETAWAY: {
        // R2857: the crates of the drop where it went down, the wake of
        // the run while the order holds.
        const cu = r.spendOrder === SignatureOrder.SIGNATURE_ORDER_GETAWAY
          ? runShare(t - r.spendAt, S.getawayCrateSec) : -1;
        if (cu >= 0) packCrates(segs, r.fromX, r.fromY, r.fromHead, cu, ink, p.k);
        if (held(r)) {
          packRunWake(segs, x, y, p.head, holdShare(on, off, S.getawayWakeInSec, S.getawayWakeOutSec),
            ink, k, F_ROUND);
        }
        return;
      }
      case SignatureOrder.SIGNATURE_ORDER_CRASH_DIVE: {
        const mul = this.feeds.noiseMul();
        if (!p.own || !held(r) || !(p.sonarPx > 0) || !(mul > 0)) return;
        const a = r.active ? 1 : 1 - off / S.footEndSec;
        if (a <= 0) return;
        packFootprint(rings, x, y, footRadius(p.sonarPx, mul, on, off), p.sonarPx, a,
          r.active ? runShare(on, S.footShrinkSec) : -1, ink);
        return;
      }
      default:
    }
  }

  /** OUTFLANK: the ghosts for every viewer, the lane for the owner. */
  private packOutflank(r: Rec, p: SignaturePose, ink: number): void {
    const t = this.time, g = r.ghosts;
    if (r.ghostN === 0 || Math.hypot(p.x - g[0], p.y - g[1]) >= S.ghostSamplePx) {
      g.copyWithin(3, 0, (TRAIL - 1) * 3);
      g[0] = p.x; g[1] = p.y; g[2] = p.head;
      r.ghostN = Math.min(TRAIL, r.ghostN + 1);
      r.ghostAt = t;
    }
    const still = 1 - clamp01((t - r.ghostAt - S.ghostStillSec) / S.ghostFadeSec);
    if (p.cell !== '' && still > 0) {
      // Walk back from the hull along the trail; ghost j stands where the
      // course is (j + 1) gaps long, and the gap scales with the hull (R2420).
      const e = this.sinks.sprite(p.cell), step = S.ghostGapPx * p.k;
      let j = 0, want = step, run = 0, ax = p.x, ay = p.y, ah = p.head;
      for (let i = 0; i < r.ghostN && j < GHOSTS; i++) {
        const bx = g[i * 3], by = g[i * 3 + 1], bh = g[i * 3 + 2], d = Math.hypot(bx - ax, by - ay);
        for (; j < GHOSTS && run + d >= want; j++, want += step) {
          const u = (want - run) / d, dh = Math.atan2(Math.sin(bh - ah), Math.cos(bh - ah));
          this.sinks.quads.sprite(ax + (bx - ax) * u, ay + (by - ay) * u, ah + dh * u, e, ink,
            ghostAlpha(j) * still * p.alpha, p.k);
        }
        run += d; ax = bx; ay = by; ah = bh;
      }
    }
    if (!p.own || r.target === 0 || (p.tx === 0 && p.ty === 0)) return;
    // The pose record is reused: read the hull before the target's pose.
    const hx = p.x, hy = p.y, sx = p.tx, sy = p.ty;
    if (!this.feeds.pose(r.target, this.them)) return;
    const tx = this.them.x, ty = this.them.y;
    if (Math.hypot(hx - sx, hy - sy) < S.laneArrivePx) {
      if (Number.isNaN(r.arriveAt)) r.arriveAt = t;
    } else r.arriveAt = NaN;
    const fade = Number.isNaN(r.arriveAt) ? 1 : 1 - (t - r.arriveAt) / S.laneFadeSec;
    if (fade <= 0) return;
    const m = sternMark(tx, ty, sx, sy, ASTERN_EDGE, this.markAt);
    const grow = outCubic(this.onAge(r) / S.laneGrowSec);
    packLane(this.sinks.segs, hx, hy, tx, ty, m.x, m.y, grow, ink, S.laneAlpha * fade);
    packSternMark(this.sinks.segs, m.x, m.y, tx, ty, ink, S.sternMarkAlpha * fade, F_ROUND);
  }

  /** BROADSIDE: the ports glow for the whole window and flash at every
   *  shot of it; after the window they fade out (R2856). */
  private packBroadside(r: Rec, p: SignaturePose, ink: number): void {
    if (!held(r)) return;
    const shot = r.shotAt >= r.onAt;
    if (r.active && !shot) {
      const foe = this.feeds.foe(p.x, p.y, p.faction, S.foeReachPx);
      if (foe !== null) r.side = sideOf(p.x, p.y, p.head, foe.x, foe.y);
    }
    const glow = holdShare(this.onAge(r), this.offAge(r), S.portGlowSec, S.portGlowSec);
    if (glow <= 0.01) return;
    const flash = shot ? runShare(this.time - r.shotAt, S.portFlashSec) : -1;
    packPorts(this.sinks.rings, this.sinks.segs, p.x, p.y, p.head, r.side, glow, flash, ink, p.k, F_ROUND);
  }

  /** The DEEP RUN gait of an own hull (R2409): the bubbles and the shroud. */
  private packDeep(r: Rec, p: SignaturePose): void {
    if (Number.isNaN(r.deepOnAt)) return;
    const t = this.time;
    const off = r.deep ? -1 : t - r.deepOffAt;
    const g = holdShare(t - r.deepOnAt, off, S.shroudInSec, S.shroudOutSec);
    if (g > 0) packShroud(this.sinks, p.x, p.y, g, p.k);
    const bu = runShare(t - r.deepOnAt, S.bubbleSec);
    if (bu >= 0) packBubbles(this.sinks.rings, p.x, p.y, bu, p.k);
  }

  draw(ctx: DrawCtx): void {
    if (!this.shown) return;
    this.sinks.quads.flush(ctx.cam);
    this.sinks.rings.flush(ctx.cam);
    this.sinks.segs.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }
}
